import {
  IAMUserSortField,
  IIAMUser,
  IIAMUserPolicyRequest,
  IIAMUserRequest,
  IIAMUserUpdateRequest,
} from "api/entities/iam-users";
import { DataService } from "./data-service";
import { IPage, IRequestParams, ITruncatedPaged } from "api/entities/page";
import { IIAMEntityRequestParams, mapEntityId } from "api/entities/iam-entity";
import {
  IAMGroupSortField,
  IIAMGroup,
  IIAMGroupPolicyRequest,
  IIAMGroupRequest,
  IIAMGroupUpdateRequest,
  IIAMGroupUserRequest,
} from "api/entities/iam-groups";
import {
  IAMPolicySortField,
  IIAMAttachedPolicy,
  IIAMPolicy,
  IIAMPolicyCreateRequest,
  IIAMPolicyUpdateRequest,
  IIAMPolicyVersion,
  IIAMPolicyVersionRequest,
} from "api/entities/iam-policies";
import {
  IIAMCredential,
  IIAMCredentialMeta,
  IIAMCredentialUpdateRequest,
} from "api/entities/iam-credentials";

class IAMService extends DataService {
  constructor() {
    super("iam", 2);
  }

  //region User
  getUser(UserName: string) {
    return this.api.get<IIAMUser>(`user/${UserName}`).then(mapUserId);
  }
  getUsers(
    params?: IIAMEntityRequestParams<IAMUserSortField>
  ): Promise<ITruncatedPaged<IIAMUser>> {
    return this.api
      .get<ITruncatedPaged<IIAMUser>>("user", params)
      .then((page) => {
        page.data = page.data.map(mapUserId);
        return page;
      });
  }
  getGroupsForUser(UserName: string): Promise<IPage<IIAMGroup>> {
    return this.api
      .get<IPage<IIAMGroup>>(`user/${UserName}/groups`)
      .then((page) => {
        page.data = page.data.map(mapGroupId);
        return page;
      });
  }
  createUser(request: IIAMUserRequest) {
    return this.api.post<IIAMUser>("user", request).then(mapUserId);
  }
  updateUser(request: IIAMUserUpdateRequest) {
    return this.api.put<IIAMUser>("user", request).then(mapUserId);
  }
  deleteUser(UserName: string) {
    return this.api.delete<void>(`user/${UserName}`);
  }

  //region Group
  createGroup(request: IIAMGroupRequest) {
    return this.api.post<IIAMGroup>("group", request).then(mapGroupId);
  }
  getGroup(GroupName: string) {
    return this.api.get<IIAMGroup>(`group/${GroupName}`).then(mapGroupId);
  }
  getGroups(
    params?: IIAMEntityRequestParams<IAMGroupSortField>
  ): Promise<ITruncatedPaged<IIAMGroup>> {
    return this.api
      .get<ITruncatedPaged<IIAMGroup>>("group", params)
      .then((page) => {
        page.data = page.data.map(mapGroupId);
        return page;
      });
  }

  updateGroup(request: IIAMGroupUpdateRequest) {
    return this.api.put<IIAMGroup>("group", request).then(mapGroupId);
  }

  deleteGroup(request: IIAMGroupRequest) {
    return this.api.delete<void>(`group/${request.GroupName}`);
  }

  addUserToGroup(request: IIAMGroupUserRequest) {
    return this.api.post("group/add-user", request);
  }

  removeUserFromGroup(request: IIAMGroupUserRequest) {
    return this.api.post("group/remove-user", request);
  }

  //region Policy
  createPolicy(request: IIAMPolicyCreateRequest) {
    return this.api.post<IIAMPolicy>("policy", request).then(mapPolicyId);
  }
  updatePolicy(request: IIAMPolicyUpdateRequest) {
    return this.api.put<IIAMPolicy>("policy", request).then(mapPolicyId);
  }
  getPolicy(arn: string) {
    return this.api
      .get<IIAMPolicy>(`policy/${encodeURIComponent(arn)}`)
      .then(mapPolicyId);
  }
  getPolicyVersion(request: IIAMPolicyVersionRequest) {
    return this.api.post<IIAMPolicyVersion>("policy/version", request);
  }
  getPolicies(params?: IIAMEntityRequestParams<IAMPolicySortField>) {
    return this.api.get<IPage<IIAMPolicy>>("policy", params).then((page) => {
      page.data = page.data.map(mapPolicyId);
      return page;
    });
  }
  deletePolicy(arn: string) {
    return this.api.delete(`policy/${encodeURIComponent(arn)}`);
  }

  attachPolicy(request: IIAMUserPolicyRequest | IIAMGroupPolicyRequest) {
    const endpoint = isUserPolicyRequest(request) ? "user" : "group";
    return this.api.post(`${endpoint}/attach-policy`, request);
  }
  detachPolicy(request: IIAMUserPolicyRequest | IIAMGroupPolicyRequest) {
    const endpoint = isUserPolicyRequest(request) ? "user" : "group";
    return this.api.post(`${endpoint}/detach-policy`, request);
  }

  listAttachedPolicies(request: IIAMUserRequest | IIAMGroupRequest) {
    let name: string;
    let endpoint: string;

    if (request.hasOwnProperty("UserName")) {
      name = (request as IIAMUserRequest).UserName;
      endpoint = "user";
    } else {
      name = (request as IIAMGroupRequest).GroupName;
      endpoint = "group";
    }

    return this.api
      .get<IPage<IIAMAttachedPolicy>>(`${endpoint}/policies/${name}`)
      .then((page) => {
        page.data = page.data.map(mapPolicyId);
        return page;
      });
  }

  //region Access Keys
  createAccessKey(request: IIAMUserRequest) {
    return this.api
      .post<IIAMCredential>("credential", request)
      .then(mapCredentialId);
  }
  async getAccessKeys(): Promise<IPage<IIAMCredentialMeta>> {
    const users = (await this.getUsers()).data;
    const pages = await Promise.all(
      users.map((u) =>
        this.getAccessKeysForUser(u.UserName).catch((err) => {
          console.warn(`Failed to load access keys for ${u.UserName}:`, err);
          return {
            data: [] as IIAMCredentialMeta[],
          } as IPage<IIAMCredentialMeta>;
        })
      )
    );
    const allKeys = pages.flatMap((page) => page.data);
    return {
      data: allKeys,
      count: allKeys.length,
    };
  }
  getAccessKeysForUser(UserName: string) {
    return this.api
      .get<IPage<IIAMCredentialMeta>>(`credential/${UserName}`)
      .then((page) => {
        page.data = page.data.map(mapCredentialId);
        return page;
      });
  }
  updateAccessKey(request: IIAMCredentialUpdateRequest) {
    return this.api.put("credential", request);
  }
  deleteAccessKey(request: IIAMCredentialUpdateRequest) {
    return this.api.delete("credential", undefined, request);
  }
}

//region Helpers
function isUserPolicyRequest(request: any): request is IIAMUserPolicyRequest {
  return (request as IIAMUserPolicyRequest).UserName !== undefined;
}

function mapUserId(user: IIAMUser): IIAMUser {
  return mapEntityId(user, "UserName");
}
function mapGroupId(group: IIAMGroup): IIAMGroup {
  return mapEntityId(group, "GroupName");
}
function mapPolicyId(policy: IIAMPolicy | IIAMAttachedPolicy): IIAMPolicy {
  if ("Arn" in policy) return mapEntityId(policy, "Arn");

  if ("PolicyArn" in policy) return mapEntityId(policy, "PolicyArn");
  return policy;
}
function mapCredentialId(
  creds: IIAMCredentialMeta | IIAMCredential
): IIAMCredentialMeta | IIAMCredential {
  return mapEntityId(creds, "AccessKeyId");
}

//region Export
export const iamService = new IAMService();
